import { YourInjuriesMedicalAttentionComponent } from "../your-injuries-medical-attention/your-injuries-medical-attention.component";
import { YourAccidentYourInvolvementComponent } from "../your-accident-your-involvement/your-accident-your-involvement.component";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { MinsterPortalProgressComponent } from "../ui-components/minster-portal-progress/minster-portal-progress.component";
import { YourAccidentTimeLocationComponent } from "../your-accident-time-location/your-accident-time-location.component";
import { MinsterConfirmDialogComponent } from '../ui-components/minster-confirm-dialog/minster-confirm-dialog.component';
import { YourAccidentDescriptionComponent } from "../your-accident-description/your-accident-description.component";
import { YourInjuriesDetailsComponent } from "../your-injuries-details/your-injuries-details.component";
import { PoliceAndWitnessesComponent } from "../police-and-witnesses/police-and-witnesses.component";
import { ResponsiblePartyComponent } from "../responsible-party/responsible-party.component";
import { YourVehicleComponent } from "../your-vehicle/your-vehicle.component";
import { EmploymentComponent } from "../employment/employment.component";
import { PassengersComponent } from '../passengers/passengers.component';
import { ScreenValidation } from '../../models/screen-validation.model';
import { PersonalComponent } from '../personal/personal.component';
import { FormName, ScreenType } from '../../models/enums.models';
import { FormGroup, FormsModule, NgForm } from '@angular/forms';
import { NavigationEnd, Router, Event } from '@angular/router';
import { DialogModel } from '../../models/dialog-model.model';
import { SubmitComponent } from "../submit/submit.component";
import { StateService } from '../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ClaimsService } from "../../services/claims.service";

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [FormsModule, PersonalComponent, PassengersComponent, SubmitComponent, MatButtonModule, EmploymentComponent, 
        YourVehicleComponent, ResponsiblePartyComponent, PoliceAndWitnessesComponent, YourAccidentYourInvolvementComponent, YourAccidentTimeLocationComponent, 
        YourAccidentDescriptionComponent, YourInjuriesDetailsComponent, YourInjuriesMedicalAttentionComponent, MinsterPortalProgressComponent],
    templateUrl: './root.component.html',
    styleUrl: './root.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class RootComponent implements AfterViewInit {
    _readonly: boolean = false;
    _currentScreen: ScreenType = ScreenType.Personal;
    _screenTypes = ScreenType;
    _screenValidation: ScreenValidation = new ScreenValidation();

    @ViewChild('mainForm') _form?: NgForm;
    @ViewChild('submitButton') _submitButton?: ElementRef;
    @ViewChild(PersonalComponent) _personalComponent?: PersonalComponent;

    constructor(
        private stateService: StateService,
        private toastr: ToastrService,
        private dialog: MatDialog,
        private router: Router,
        private _change: ChangeDetectorRef,
        protected claimsService: ClaimsService
    ) {
        this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationEnd) {
                let screen = event.url.split('/')[2];
                this._currentScreen = ScreenType[screen as keyof typeof ScreenType];
                this._change.markForCheck(); 

                //If the form is submitted and we hit the summary screen we need to revalidated so any failed(red) areas are updated
                if (this._currentScreen == ScreenType.Summary && this._form?.submitted) {
                    this.validateScreens();
                }
            }
        });
 
        this._readonly =  this.stateService.formIsSubmitted;
    }
    
    //Event to disable all UI controls
    ngAfterViewInit(): void {
        if (this._readonly) {
            this.stateService.$formDisabled.next(true);
            this._change.markForCheck();
        }
    }

    onPrev(): void {
        this.router.navigate(['form', 'PoliceAndWitnesses']);
    }
 
    onSubmit(form: NgForm): void {   

        this.markFormGroupTouched(form);
        if (!form.valid) {
            this.toastr.info('There is some missing or invalid data, any sections with errors will be highlighted red in the summary panel');
            this.validateScreens();
            return;
        }

        const claimReference = this.stateService.claim?.claimReference;

        if (claimReference) {
                this.claimsService.saveClaimSummary(claimReference).subscribe({
                    next: (response) => {
                        this.stateService.formIsSubmitted = true;  
                        this.router.navigate(['scripting']);
                    },
                    error: (error) =>  this.toastr.info('There is some missing or invalid data, any sections with errors will be highlighted red in the summary panel', error) 
                });
            } else {
                console.error('Claim reference is undefined'); 
        }  
    }

    showStatementOfTruth(): void { 

        this._form!.disabled;
        let message = 'You are about to confirm all the information you have registered so far. Please check that everything is accurate before you continue. Once submitted you can not go back and edit.';
        this.dialog.open(MinsterConfirmDialogComponent, { 
            maxWidth: "600px", 
            data: new DialogModel('', message) 
        }).afterClosed().subscribe(async (result) => {
            if (result) {                 
                this._submitButton!.nativeElement.click(); 
            }
        });
    }

    validateScreens(): void {
        this._screenValidation.personal = (this._form!.controls[FormName.Personal]! as FormGroup).valid;
        this._screenValidation.employment = (this._form!.controls[FormName.Employment]! as FormGroup).valid;
        this._screenValidation.yourAccidentYourInvolvement = (this._form!.controls[FormName.YourAccidentInvolvement]! as FormGroup).valid;
        this._screenValidation.yourAccidentYourTimeLocation = (this._form!.controls[FormName.YourAccidentLocation]! as FormGroup).valid;
        this._screenValidation.yourAccidentDescription = (this._form!.controls[FormName.YourAccidentDescription]! as FormGroup).valid;
        this._screenValidation.yourInjuriesDetails = (this._form!.controls[FormName.YourInjuriesDetails]! as FormGroup).valid;
        this._screenValidation.yourInjuriesMedicalAttention = (this._form!.controls[FormName.YourInjuriesMedicalAttention]! as FormGroup).valid;
        this._screenValidation.yourVehicle = (this._form!.controls[FormName.YourVehicle]! as FormGroup).valid;
        this._screenValidation.responsibleParty = (this._form!.controls[FormName.ResponsibleParty]! as FormGroup).valid;
        this._screenValidation.policeAndWitnesses = (this._form!.controls[FormName.PoliceAndWitnesses]! as FormGroup).valid;
        this._screenValidation.passengers = (this._form!.controls[FormName.Passengers]! as FormGroup).valid;
    }

    //This is needed to validate the forms before submission
    private markFormGroupTouched(formGroup: FormGroup | NgForm): void {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.controls[key];
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } 
            else {
                control.markAsDirty();
                control.markAsTouched();
                control.updateValueAndValidity();
            }
        });
    }
}
